// src/services/api.js
import { API_BASE_URL, API_ENDPOINTS } from "../config/api";

const API_TOKEN = process.env.REACT_APP_API_TOKEN;

// Helper function for API requests
export const makeApiRequest = async (url, method = "GET", body = null) => {
  // Get token from localStorage
  const accessToken = localStorage.getItem("accessToken");

  // Use localStorage token if available, otherwise use environment variable
  const token = accessToken || API_TOKEN;

  // Check if a token is available
  if (!token) {
    console.error("No authentication token available");
    throw new Error("Authentication required. Please log in.");
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const config = {
    method,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API Error Response (${response.status}):`, errorText);

      // Try to parse error message
      let errorMessage = `API request failed with status ${response.status}`;
      let errorData = null;

      try {
        errorData = JSON.parse(errorText);
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = {
        status: response.status,
        data: errorData,
        text: errorText,
      };
      throw error;
    }

    // Check if response has content before trying to parse
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const jsonResponse = await response.json();
      return jsonResponse;
    }

    const textResponse = await response.text();
    return textResponse;
  } catch (error) {
    console.error(`Error in API request to ${url}:`, error);
    throw error;
  }
};

/**
 * Fetches articles from the API
 * @param {number} page - The page number to fetch
 * @param {string} search - Optional search term
 * @param {Object} sort - Optional sort criteria { field: string, order: string }
 * @returns {Promise} - The API response
 */
export const fetchArticles = async (
  page = 1,
  search = "",
  sort = { field: "created_at", order: "desc" }
) => {
  try {
    let url = `${API_ENDPOINTS.ARTICLES}?page=${page}`;

    // Add search parameter if provided
    if (search) {
      url += `&title=${encodeURIComponent(search)}`;
    }

    // Add sorting parameters
    if (sort && sort.field) {
      url += `&ordering=${sort.order === "desc" ? "-" : ""}${sort.field}`;
    }

    return await makeApiRequest(url);
  } catch (error) {
    console.error("Error fetching articles:", error);
    throw error;
  }
};

/**
 * Fetches scripts from the API
 * @param {number} page - The page number to fetch
 * @param {string} search - Optional search term
 * @returns {Promise} - The API response
 */
export const fetchScripts = async (page = 1, search = "") => {
  try {
    let url = `${API_ENDPOINTS.SCRIPTS}?page=${page}`;

    // Add search parameter if provided
    if (search) {
      url += `&name=${encodeURIComponent(search)}`;
    }

    return await makeApiRequest(url);
  } catch (error) {
    console.error("Error fetching scripts:", error);
    throw error;
  }
};

/**
 * Generates a script for a given article
 * @param {Object} payload - Payload containing article_id and script_name
 * @returns {Promise} - The API response
 */
export const generateScript = async (payload) => {
  try {
    const response = await makeApiRequest(
      API_ENDPOINTS.GENERATE_SCRIPT,
      "POST",
      payload
    );

    // Check if response is HTML (indicating server error page)
    if (typeof response === "string" && response.includes("<!DOCTYPE html>")) {
      throw new Error(
        "Server encountered an internal error. Please try again later."
      );
    }

    return response;
  } catch (error) {
    console.error("Error generating script:", error);
    // Provide more specific error message
    if (error.response?.status === 500) {
      throw new Error(
        "Server encountered an internal error. Please try again later."
      );
    }
    throw error;
  }
};

/**
 * Fetches script details by ID
 * @param {number|string} scriptId - ID of the script to fetch
 * @returns {Promise} - The API response
 */
export const fetchScriptDetails = async (scriptId) => {
  try {
    // Ensure we're using a valid ID format
    const id = String(scriptId).trim();

    if (!id) {
      throw new Error("Script ID is required");
    }

    const url = API_ENDPOINTS.SCRIPT_DETAIL(id);

    // Get the result from the API
    const result = await makeApiRequest(url);

    // Return the result
    return result;
  } catch (error) {
    console.error(`Error fetching script details for ID ${scriptId}:`, error);

    // If the error is a response with status, provide more helpful error message
    if (error.response && error.response.status) {
      if (error.response.status === 404) {
        throw new Error(`Script with ID ${scriptId} not found`);
      } else if (
        error.response.status === 401 ||
        error.response.status === 403
      ) {
        throw new Error("Authentication error. Please check your API token");
      } else if (error.response.status >= 500) {
        throw new Error("Server error. Please try again later");
      }
    }

    // If we don't have a specific error message, throw the original error
    throw error;
  }
};

/**
 * Fetches published scripts from the API
 * @param {number} page - The page number to fetch
 * @param {string} search - Optional search term
 * @returns {Promise} - The API response
 */
export const fetchPublishedScripts = async (page = 1, search = "") => {
  try {
    let url = `${API_ENDPOINTS.PUBLISHED_SCRIPTS}?page=${page}`;

    // Add search parameter if provided
    if (search) {
      url += `&name=${encodeURIComponent(search)}`;
    }

    return await makeApiRequest(url);
  } catch (error) {
    console.error("Error fetching published scripts:", error);
    throw error;
  }
};

/**
 * Uploads an article
 * @param {Object} articleData - Article data to be uploaded
 * @returns {Promise} - The API response
 */
export const uploadArticle = async (articleData) => {
  try {
    return await makeApiRequest(API_ENDPOINTS.ARTICLES, "POST", articleData);
  } catch (error) {
    console.error("Error uploading article:", error);
    throw error;
  }
};

/**
 * Updates an existing article
 * @param {number} articleId - ID of the article to update
 * @param {Object} articleData - Updated article data
 * @returns {Promise} - The API response
 */
export const updateArticle = async (articleId, articleData) => {
  try {
    const url = API_ENDPOINTS.ARTICLE_DETAIL(articleId);

    return await makeApiRequest(url, "PATCH", articleData);
  } catch (error) {
    console.error(`Error updating article ${articleId}:`, error);
    throw error;
  }
};

/**
 * Deletes an article
 * @param {number} articleId - ID of the article to delete
 * @returns {Promise} - The API response
 */
export const deleteArticle = async (articleId) => {
  try {
    // Use the script endpoint with pk parameter
    const url = `${API_ENDPOINTS.ARTICLES}?pk=${articleId}`;

    // Make sure to pass the ID in the request body as well
    const body = { id: articleId };
    return await makeApiRequest(url, "DELETE", body);
  } catch (error) {
    console.error(`Error deleting article ${articleId}:`, error);
    throw error;
  }
};

/**
 * Fetches article details by ID
 * @param {number} articleId - ID of the article to fetch
 * @returns {Promise} - The API response
 */
export const fetchArticleDetails = async (articleId) => {
  try {
    const url = API_ENDPOINTS.ARTICLE_DETAIL(articleId);

    return await makeApiRequest(url);
  } catch (error) {
    console.error(`Error fetching details for article ${articleId}:`, error);
    throw error;
  }
};

/**
 * Bulk upload articles from a CSV or file
 * @param {File} file - File to be uploaded
 * @returns {Promise} - The API response
 */
export const bulkUploadArticles = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    // Get token from localStorage or fall back to environment variable
    const accessToken = localStorage.getItem("accessToken");
    const token = accessToken || API_TOKEN;

    // Check if a token is available
    if (!token) {
      console.error("No authentication token available");
      throw new Error("Authentication required. Please log in.");
    }

    const response = await fetch(API_ENDPOINTS.BULK_UPLOAD_ARTICLES, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Bulk Upload API Error Response:", errorText);
      throw new Error(`Bulk upload failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error in bulk upload:", error);
    throw error;
  }
};

/**
 * Fetches and saves a single article from a URL
 * @param {string} url - URL of the article to fetch
 * @returns {Promise} - The API response
 */
export const fetchSaveSingleArticle = async (url) => {
  try {
    // Match the exact format shown in the API image: { "url": "https://..." }
    const payload = { url: url };

    return await makeApiRequest(
      API_ENDPOINTS.FETCH_SAVE_SINGLE_ARTICLE,
      "POST",
      payload
    );
  } catch (error) {
    console.error("Error fetching and saving single article:", error);
    throw error;
  }
};

/**
 * Fetches and saves articles from a custom URL
 * @param {Object} payload - Object containing custom_url, max_number, and single_article flag
 * @returns {Promise} - The API response
 */
export const fetchSaveArticles = async (payload) => {
  try {
    // If this is a single article URL, make sure we send the right flags
    if (payload.single_article) {
      payload.max_number = 1; // Ensure max_number is 1 for single article
    }

    // Ensure max_number is included in payload and properly formatted as a number
    if (payload.max_number) {
      payload.max_number = parseInt(payload.max_number, 10);
    }

    return await makeApiRequest(
      API_ENDPOINTS.FETCH_SAVE_ARTICLES,
      "POST",
      payload
    );
  } catch (error) {
    console.error("Error fetching and saving articles:", error);
    throw error;
  }
};

/**
 * Fetches videos from the API
 * @param {number} page - The page number to fetch
 * @param {string} search - Search term for filtering videos by script content
 * @param {Object} sort - Optional sort criteria { field: string, order: string }
 * @returns {Promise} - The API response
 */
export const fetchVideos = async (
  page = 1,
  search = "",
  sort = { field: "created_at", order: "desc" }
) => {
  try {
    let url = `${API_ENDPOINTS.VIDEOS}?page=${page}`;

    // Add search parameter if provided - will search across script content and metadata
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }

    // Add sorting parameters
    if (sort?.field) {
      url += `&ordering=${sort.order === "desc" ? "-" : ""}${sort.field}`;
    }

    return await makeApiRequest(url);
  } catch (error) {
    console.error("Error fetching videos:", error);
    throw error;
  }
};

/**
 * Generate a video from a script
 * @param {Object} videoData - Video generation parameters (script_id, avatar_id, voice_id, orientation, speed)
 * @returns {Promise} - The API response
 */
export const generateVideo = async (videoData) => {
  try {
    return await makeApiRequest(
      API_ENDPOINTS.GENERATE_VIDEO,
      "POST",
      videoData
    );
  } catch (error) {
    console.error("Error generating video:", error);

    // Check for the specific error message about already generated/processing videos
    if (error.response && error.response.status === 400) {
      // Check the error text for specific patterns
      const errorText = error.response.text || "";

      if (
        errorText.includes("already generated") ||
        errorText.includes("in processing") ||
        errorText.includes("already exists")
      ) {
        // Create a more user-friendly error
        const friendlyError = new Error(
          "Script is already generated or is in processing"
        );
        friendlyError.isAlreadyProcessing = true;
        friendlyError.originalError = error;
        throw friendlyError;
      }
    }

    throw error;
  }
};

/**
 * Get video details by ID
 * @param {number} videoId - The ID of the video to fetch
 * @returns {Promise} - The API response
 */
export const getVideoDetails = async (videoId) => {
  try {
    const url = API_ENDPOINTS.VIDEO_DETAIL(videoId);

    return await makeApiRequest(url);
  } catch (error) {
    console.error(`Error fetching details for video ${videoId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing script
 * @param {number} scriptId - ID of the script to update
 * @param {Object} scriptData - Updated script data
 * @returns {Promise} - The API response
 */
export const updateScript = async (scriptId, scriptData) => {
  try {
    const url = API_ENDPOINTS.SCRIPT_DETAIL(scriptId);

    return await makeApiRequest(url, "PATCH", scriptData);
  } catch (error) {
    console.error(`Error updating script ${scriptId}:`, error);
    throw error;
  }
};

/**
 * Deletes a script
 * @param {number} scriptId - ID of the script to delete
 * @returns {Promise} - The API response
 */
export const deleteScript = async (scriptId) => {
  try {
    const url = API_ENDPOINTS.SCRIPT_DETAIL(scriptId);
    return await makeApiRequest(url, "DELETE");
  } catch (error) {
    console.error(`Error deleting script ${scriptId}:`, error);
    throw error;
  }
};

/**
 * Fetches prompt templates from the API
 * @returns {Promise} - The API response
 */
export const fetchPromptTemplates = async () => {
  try {
    const url = API_ENDPOINTS.PROMPT_TEMPLATES;
    return await makeApiRequest(url);
  } catch (error) {
    console.error("Error fetching prompt templates:", error);
    throw error;
  }
};

/**
 * Regenerates a script with a selected prompt template
 * @param {Object} scriptData - The script object to regenerate
 * @param {Object} promptTemplate - The prompt template to use for regeneration
 * @returns {Promise} - The API response with the regenerated script
 */
export const regenerateScript = async (scriptData, promptTemplate) => {
  try {
    // Ensure script_id is converted to a number
    const scriptId = parseInt(scriptData.id, 10);
    const promptTemplateId = parseInt(promptTemplate.id, 10);

    if (isNaN(scriptId) || isNaN(promptTemplateId)) {
      throw new Error(
        `Invalid IDs - script_id: ${scriptData.id}, prompt_template_id: ${promptTemplate.id}`
      );
    }

    if (!promptTemplate.prompt) {
      throw new Error(
        "Prompt text is required but was not provided in the template"
      );
    }

    // Create payload with both script_id and prompt fields as required by the API
    const payload = {
      script_id: scriptId,
      prompt_template_id: promptTemplateId,
      prompt: promptTemplate.prompt, // The prompt text itself
      prompt_name: promptTemplate.name, // Include the prompt name for reference
    };

    return await makeApiRequest(
      API_ENDPOINTS.REGENERATE_SCRIPT,
      "POST",
      payload
    );
  } catch (error) {
    console.error("Error regenerating script:", error);
    throw error;
  }
};

/**
 * Regenerates a script with a custom prompt
 * @param {Object} scriptData - The script object to regenerate
 * @param {string} customPrompt - The custom prompt text entered by the user
 * @param {Object} promptTemplate - The original prompt template (optional)
 * @returns {Promise} - The API response with the regenerated script
 */
export const regenerateScriptWithCustomPrompt = async (
  scriptData,
  customPrompt,
  promptTemplate = null
) => {
  try {
    // Ensure script_id is converted to a number
    const scriptId = parseInt(scriptData.id, 10);

    if (isNaN(scriptId)) {
      throw new Error(`Invalid script ID: ${scriptData.id}`);
    }

    if (!customPrompt || customPrompt.trim() === "") {
      throw new Error("Custom prompt text is required");
    }

    // Create payload with script_id and custom prompt
    const payload = {
      script_id: scriptId,
      prompt: customPrompt.trim(),
    };

    // If a prompt template was used as base, include its ID for reference
    if (promptTemplate && promptTemplate.id) {
      payload.prompt_template_id = parseInt(promptTemplate.id, 10);
      payload.prompt_name = promptTemplate.name;
    } else {
      payload.prompt_name = "Custom Prompt";
    }

    return await makeApiRequest(
      API_ENDPOINTS.REGENERATE_SCRIPT,
      "POST",
      payload
    );
  } catch (error) {
    console.error("Error regenerating script with custom prompt:", error);
    throw error;
  }
};

/**
 * Deletes a video
 * @param {string|number} videoId - ID of the video to delete
 * @returns {Promise} - The API response
 */
export const deleteVideo = async (videoId) => {
  try {
    // Make sure videoId is in the right format - convert to string for URL
    const idString = String(videoId);

    // Use the base videos endpoint with pk parameter approach
    const url = `${API_ENDPOINTS.VIDEOS}?pk=${idString}`;

    // Make the API request with DELETE method and include the ID in the body as well
    return await makeApiRequest(url, "DELETE", { id: videoId });
  } catch (error) {
    console.error(`Error deleting video ${videoId}:`, error);
    throw error;
  }
};

/**
 * Batch delete multiple videos
 * @param {Array<string>} videoIds - Array of video IDs to delete
 * @returns {Promise<Array>} - Array of responses for each deletion
 */
export const batchDeleteVideos = async (videoIds) => {
  try {
    const results = await Promise.allSettled(
      videoIds.map((id) => deleteVideo(id))
    );

    return results.map((result, index) => ({
      videoId: videoIds[index],
      success: result.status === "fulfilled",
      error: result.status === "rejected" ? result.reason : null,
    }));
  } catch (error) {
    console.error("Error in batch delete videos:", error);
    throw error;
  }
};

// Export all functions as a default object
export default {
  makeApiRequest,
  fetchArticles,
  fetchScripts,
  generateScript,
  fetchScriptDetails,
  fetchPublishedScripts,
  uploadArticle,
  updateArticle,
  deleteArticle,
  fetchArticleDetails,
  bulkUploadArticles,
  fetchSaveArticles,
  fetchSaveSingleArticle,
  fetchVideos,
  generateVideo,
  getVideoDetails,
  updateScript,
  deleteScript,
  fetchPromptTemplates,
  regenerateScript,
  regenerateScriptWithCustomPrompt,
  deleteVideo,
  batchDeleteVideos,
};
